import { useRef, useState, useCallback, type ImgHTMLAttributes } from "react";

export interface ImageZoomProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Disable zoom and just render a plain <img>. */
  disabled?: boolean;
}

/**
 * Click-to-zoom <img/>. Renders a plain image during SSR / before hydration,
 * then upgrades to a native dialog zoom on the client.
 */
export function ImageZoom({ disabled, alt = "", src, ...imgProps }: ImageZoomProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled || !dialogRef.current) return;
    dialogRef.current.showModal();
    setOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    if (!dialogRef.current) return;
    dialogRef.current.close();
    setOpen(false);
  }, []);

  const img = (
    <img
      alt={alt}
      src={src}
      {...imgProps}
      onClick={disabled ? undefined : handleClick}
      style={{
        cursor: disabled ? undefined : "zoom-in",
        ...imgProps.style,
      }}
    />
  );

  if (disabled) return img;

  return (
    <>
      {img}
      <dialog
        ref={dialogRef}
        onClick={(e) => {
          if (e.target === dialogRef.current) handleClose();
        }}
        className="rmiz-dialog-reset"
      >
        <img
          src={src}
          alt={alt}
          style={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            objectFit: "contain",
            cursor: "zoom-out",
          }}
          onClick={handleClose}
        />
      </dialog>
      <style>{`
        .rmiz-dialog-reset {
          border: none;
          padding: 0;
          margin: auto;
          background: transparent;
          max-width: 100vw;
          max-height: 100vh;
        }
        .rmiz-dialog-reset::backdrop {
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(4px);
        }
        .rmiz-dialog-reset[open] {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </>
  );
}
